import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { apiClient } from "../lib/apiClient";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { ContactsSection } from "../components/ContactsSection";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "../components/ui/carousel";

export default function AboutUsPage() {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sections, setSections] = React.useState([]);
  const [intro, setIntro] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState(0);
  const [carouselApi, setCarouselApi] = React.useState(null);

  React.useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        setLoading(true);
        
        // Fetch intro
        const introResponse = await apiClient.get("/public/about-intro", {
          params: { lang },
        });
        setIntro(introResponse.data);
        
        // Fetch sections
        const sectionsResponse = await apiClient.get("/public/about-sections", {
          params: { lang },
        });
        setSections(sectionsResponse.data);
      } catch (err) {
        console.error("Failed to load About Us content", err);
        setSections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, [lang]);

  // Update page meta tags
  React.useEffect(() => {
    const title = lang === "en" 
      ? "About Us | Strategic Lobbying Alliance" 
      : "Про нас | Strategic Lobbying Alliance";
    
    const description = lang === "en"
      ? "Learn about Strategic Lobbying Alliance's mission, values, objectives and scope of activities in promoting ethical lobbying standards globally."
      : "Дізнайтеся про місію, цінності, цілі та сферу діяльності Strategic Lobbying Alliance у просуванні етичних стандартів лобізму у світі.";

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description;
  }, [lang]);

  // Sync carousel with tabs
  React.useEffect(() => {
    if (!carouselApi) return;

    carouselApi.on("select", () => {
      setActiveTab(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  // Sync tabs with carousel
  React.useEffect(() => {
    if (carouselApi) {
      carouselApi.scrollTo(activeTab);
    }
  }, [activeTab, carouselApi]);

  const handleNavClick = (anchor) => {
    navigate("/", { state: { scrollTo: anchor } });
    setMobileOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-200 text-slate-900 flex flex-col">
        <SiteHeader
          lang={lang}
          onLangChange={setLang}
          onNavClick={handleNavClick}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          showNavigation={true}
        />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-slate-600">
            {lang === "en" ? "Loading..." : "Завантаження..."}
          </p>
        </main>
        <SiteFooter lang={lang} />
      </div>
    );
  }

  if (!loading && sections.length === 0) {
    return (
      <div className="min-h-screen bg-slate-200 text-slate-900 flex flex-col">
        <SiteHeader
          lang={lang}
          onLangChange={setLang}
          onNavClick={handleNavClick}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          showNavigation={true}
        />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-slate-600">
            {lang === "en" ? "Content not available" : "Контент недоступний"}
          </p>
        </main>
        <SiteFooter lang={lang} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 flex flex-col">
      <SiteHeader
        lang={lang}
        onLangChange={setLang}
        onNavClick={handleNavClick}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        showNavigation={true}
      />
      
      <main className="flex-1">
        {/* Intro Section */}
        {intro && (
          <section className="bg-white py-16 sm:py-20">
            <div className="mx-auto max-w-6xl px-4 md:px-6">
              <div className="space-y-6">
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
                  {intro.title}
                </h1>
                <div className="space-y-4">
                  {intro.paragraphs && intro.paragraphs.map((paragraph, idx) => (
                    <p key={idx} className="text-base leading-relaxed text-slate-700 md:text-lg">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Tab Content */}
        <section className="bg-white pt-0 pb-16 sm:pb-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            {/* Tabs Navigation - positioned above content */}
            <div className="flex justify-between border-b border-slate-200 pb-4 mb-8">
              {sections.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => setActiveTab(idx)}
                  className={`text-lg font-semibold transition-colors flex-1 text-center md:text-xl ${
                    activeTab === idx
                      ? "text-amber-600"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>

            {/* Carousel for swipeable content */}
            <Carousel 
              setApi={setCarouselApi}
              opts={{ 
                loop: false,
                watchDrag: true
              }}
              className="w-full"
            >
              <CarouselContent>
                {sections.map((section, index) => (
                  <CarouselItem key={section.id}>
                    {/* Content with overlapping layout and animations */}
                    <div className="relative">
                      <div className="grid md:grid-cols-2 gap-0">
                        {/* Left: Image with slide-in animation */}
                        <div className="order-2 md:order-1 relative z-0 animate-slide-in-left">
                          {section.image_url ? (
                            <div className="h-full min-h-[400px]">
                              <img 
                                src={section.image_url} 
                                alt={section.title}
                                className="h-full w-full object-cover rounded-2xl md:rounded-l-2xl md:rounded-r-none"
                              />
                            </div>
                          ) : (
                            <div className="h-full min-h-[400px] bg-slate-200 flex items-center justify-center rounded-2xl md:rounded-l-2xl md:rounded-r-none">
                              <p className="text-slate-400 text-sm">
                                {lang === "en" ? "No image available" : "Зображення відсутнє"}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Right: Content in Glass Box - overlapping left with delayed animation */}
                        <div className="order-1 md:order-2 relative z-10 md:-ml-20 flex items-center animate-slide-in-right">
                          <div className="relative rounded-2xl border border-slate-300/70 p-8 shadow-[0_22px_60px_rgba(15,23,42,0.4)] backdrop-blur-sm w-full" style={{background: 'linear-gradient(135deg, rgba(225,220,218,0.92) 0%, rgba(218,215,213,0.94) 20%, rgba(210,213,215,0.95) 45%, rgba(205,212,216,0.96) 70%, rgba(200,210,215,0.97) 85%, rgba(235,238,240,0.98) 100%)'}}>
                            <div className="absolute inset-0 rounded-2xl border border-white/80" />
                            <div className="relative space-y-4 about-content">
                              <div 
                                className="text-sm leading-relaxed text-slate-900 md:text-base font-medium"
                                dangerouslySetInnerHTML={{ __html: section.content }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
              ))}
              </CarouselContent>
            </Carousel>

            {/* Back Button */}
            <div className="mt-8">
              <Button
                onClick={() => navigate("/")}
                className="group inline-flex items-center gap-2 rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
              >
                {lang === "en" ? "← Back to Homepage" : "← Повернутися на головну"}
              </Button>
            </div>
          </div>
        </section>

        <ContactsSection id="contacts" lang={lang} />
      </main>
      
      <SiteFooter lang={lang} />
    </div>
  );
}
